import { getCompositeMetricIds } from 'components/Studio/compositeUtils';
import { isKpiRow } from 'hooks/kpiTypeCheckers';
import cardTypeCheckers from 'types/cardTypeCheckers';

export type ContentInput =
  | { contentType: 'Dashboard'; content: DashboardType }
  | { contentType: 'Report'; content: ReportType }
  | { contentType: 'Scorecard'; content: Scorecards.Scorecard }
  | {
      contentType: 'Board';
      content: Board | PerformanceBoardTypes.Board;
    }
  | { contentType: 'Goal'; content: GeneralGoal };

export interface ContentMetricIdLookups {
  dashboardGadgetsLookup: { [id: string]: DashboardGadget | undefined };
  scorecardsLookup: { [id: string]: Scorecards.Scorecard | undefined };
  metricListsLookup: { [id: string]: MetricListGadgetType | undefined };
  reportGadgetsLookup: {
    [id: string]: ReportGadgets.ReportGadget | undefined;
  };
  goals: GeneralGoal[];
  getMetricIds: (vis?: ExtendedVisualisationDefinition) => string[];
}

// Every metric id a piece of content references, de-duped.
//
// Pure, and takes its lookups as arguments, so it can be run over hundreds of
// items in one pass (the AI-ready coverage index) as well as for the single
// piece of content MetricsGate is waiting on. useContentMetricIds is the hook
// wrapper — keep this the only implementation, because a second copy that
// drifts would make coverage silently wrong.
//
// NOTE: legacy `ChartDefinition` cards are deliberately not traversed. They
// pre-date report/dashboard gadgets and MetricsGate has never gated on them;
// counting them here but not there would make the two disagree about what is
// loaded. Both sides of any coverage ratio omit them, so the ratio is unbiased.
const buildContentMetricIds = ({
  input,
  lookups,
}: {
  // Kept as a nested union rather than spread flat: spreading a discriminated
  // union into a wider object literal loses the discrimination, and the only
  // way back is a cast.
  input: ContentInput;
  lookups: ContentMetricIdLookups;
}): string[] => {
  const { content, contentType } = input;
  const {
    dashboardGadgetsLookup,
    scorecardsLookup,
    metricListsLookup,
    reportGadgetsLookup,
    goals,
    getMetricIds,
  } = lookups;
  const ids: string[] = [];

  if (contentType === 'Scorecard') {
    content.kpis.filter(isKpiRow).forEach((k) => ids.push(k.metricId));
  } else if (contentType === 'Board') {
    if ('metricIds' in content && content.metricIds) {
      ids.push(...content.metricIds);
    }
    // Extract metric IDs from slideout sections
    const sections =
      'sidePanel' in content
        ? content.sidePanel
        : 'slideout' in content
          ? content.slideout
          : [];
    sections.forEach((section) => {
      if (
        'metricIds' in section &&
        Array.isArray((section as { metricIds?: string[] }).metricIds)
      ) {
        ids.push(...(section as { metricIds: string[] }).metricIds);
      }
    });
  } else if (contentType === 'Goal') {
    ids.push(content.metricId);
    content.kpis.forEach((k) => ids.push(k.metricId));
  } else if (contentType === 'Dashboard' || contentType === 'Report') {
    content.canvas.cards.forEach((card) => {
      if (cardTypeCheckers.isDashboardGadget(card)) {
        const gadget = dashboardGadgetsLookup[card.content.dashboardGadgetId];
        if (gadget) {
          ids.push(...getMetricIds(gadget.chartDef));
        }
      } else if (cardTypeCheckers.isScorecard(card)) {
        const scorecard = scorecardsLookup[card.content.scorecardId];
        if (scorecard) {
          ids.push(...getMetricIds(scorecard));
        }
      } else if (cardTypeCheckers.isMetricList(card)) {
        const metricList = metricListsLookup[card.content.metricListId];
        if (metricList) {
          ids.push(...getMetricIds(metricList));
        }
      } else if (cardTypeCheckers.isReportGadget(card)) {
        const reportGadget = reportGadgetsLookup[card.content.reportGadgetId];
        if (reportGadget) {
          ids.push(...getMetricIds(reportGadget.chartDef));
        }
      } else if (cardTypeCheckers.isGoal(card)) {
        const goal = goals.find((g) => g.id === card.content.goalId);
        if (goal) {
          ids.push(...getMetricIds(goal));
        }
      } else if (cardTypeCheckers.isStudioComposite(card)) {
        // Stored inline on the canvas, so there's no gadget to look up
        ids.push(...getCompositeMetricIds(card.content.composite));
      }
    });
  }

  return [...new Set(ids.filter(Boolean))];
};

export default buildContentMetricIds;
